import { useRef, useEffect, useState, useCallback } from "react";

export default function About() {
  const timelineRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const journey = [
    { year: "2017", text: "LegendImpex was founded with a vision to revive leather artistry." },
    { year: "2018", text: "Launched our first collection of premium leather bags and belts." },
    { year: "2019", text: "Expanded into international markets." },
    { year: "2020", text: "Introduced sustainable leather products." },
    { year: "2021", text: "Collaborated with artisans globally for unique designs." },
    { year: "2023", text: "Recognized as a trusted luxury leather brand worldwide." },
    { year: "2025", text: "Launched our online platform for seamless shopping experience." },
  ];

  // Scroll timeline to a specific index
  const scrollTimeline = (index) => {
    if (timelineRef.current) {
      const itemWidth = 280; // width of each card + gap
      const scrollAmount = itemWidth * index;
      timelineRef.current.scrollTo({ left: scrollAmount, behavior: "smooth" });
    }
  };

  // Navigate timeline
  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => {
      const nextIndex = (prevIndex + 1) % journey.length;
      scrollTimeline(nextIndex);
      return nextIndex;
    });
  }, [journey.length]);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => {
      const prev = prevIndex === 0 ? journey.length - 1 : prevIndex - 1;
      scrollTimeline(prev);
      return prev;
    });
  };

  // Auto-scroll every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      handleNext();
    }, 3000);
    return () => clearInterval(interval);
  }, [handleNext]);

  // Drag-to-scroll
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - timelineRef.current.offsetLeft);
    setScrollLeft(timelineRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - timelineRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    timelineRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => setIsDragging(false);

  return (
    <div>
      {/* Hero Section */}
      <div
        className="text-white text-center d-flex align-items-center justify-content-center hero-about"
        style={{
          backgroundImage: "url('/images/craftman5.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: "65vh",
          position: "relative",
        }}
      >
        <div className="overlay"></div>
        <div style={{ position: "relative", zIndex: 2, animation: "fadeIn 2s ease" }}>
          <h1 className="display-4 fw-bold hero-title">About LegendImpex</h1>
          <p className="lead">Crafting Luxury Leather Since 2017</p>
        </div>
      </div>

      {/* Intro */}
      <div className="container my-5">
        <p className="lead text-center mb-5">
          Since <span className="highlight">2017</span>, <span className="highlight">LegendImpex</span> has
          redefined leather craftsmanship blending tradition with modern luxury.  
          We create <span className="italic">timeless pieces</span> that reflect elegance, durability, and unmatched quality.
        </p>

        {/* Highlights */}
        <div className="row text-center mb-5 g-4">
          {[
            { icon: "fas fa-gem text-warning", title: "Premium Quality", desc: "Ethically sourced leather, carefully crafted to ensure luxury and long-lasting performance." },
            { icon: "fas fa-hand-paper text-primary", title: "Handcrafted Elegance", desc: "Each product is handmade by artisans, blending passion and tradition in every detail." },
            { icon: "fas fa-leaf text-success", title: "Sustainable Luxury", desc: "Committing to eco-friendly practices, we design products that last while caring for the planet." },
          ].map((item, i) => (
            <div className="col-md-4" key={i}>
              <div className="p-4 border rounded shadow-sm h-100 about-card">
                <i className={`${item.icon} fa-2x mb-3`}></i>
                <h4 className="fw-bold">{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <h2 className="section-heading text-center mb-4">Our Journey</h2>
        <div className="timeline-wrapper position-relative mb-5">
          <button className="timeline-arrow left" onClick={handlePrev}>&lt;</button>
          <div
            className="timeline-horizontal"
            ref={timelineRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {journey.map((item, i) => (
              <div className={`timeline-item ${currentIndex === i ? "active" : ""}`} key={i}>
                <h5>{item.year}</h5>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
          <button className="timeline-arrow right" onClick={handleNext}>&gt;</button>
        </div>

        {/* CEO Section */}
        <div className="text-center">
          <div className="p-5 shadow rounded bg-light d-inline-block ceo-card">
            {/* <img
              src="/images/ceo.jpg"
              alt="CEO"
              style={{
                width: "130px",
                height: "130px",
                borderRadius: "50%",
                objectFit: "cover",
                marginBottom: "15px",
                border: "4px solid #ff6a00",
              }}
            /> */}
            <h5><strong>Sachneet Singh</strong></h5>
            <p className="text-muted">CEO, LegendImpex</p>
            <blockquote className="lead text-center mb-5">
              "At LegendImpex, we don’t just make leather products, we create heritage you can carry."
            </blockquote>
          </div>
        </div>
      </div>

      {/* CSS */}
      <style>{`
        .overlay {
          position: absolute; top: 0; left: 0; right: 0; bottom: 0;
          background: linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.7));
        }
        .hero-title {
          background: linear-gradient(to right, #ffdd00, #ff6a00);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .highlight { color: #ff6a00; font-weight: 600; }
        .italic { font-style: italic; color: #444; }
        .about-card { transition: all 0.3s ease-in-out; }
        .about-card:hover { transform: translateY(-8px) scale(1.02); background-color: #fdfdfd; box-shadow: 0 10px 25px rgba(0,0,0,0.2); }
        .section-heading {
          font-size: 2rem; font-weight: 700; position: relative; display: inline-block;
        }
        .section-heading::after {
          content: ""; display: block; width: 60px; height: 4px; background: #ff6a00; margin: 10px auto 0; border-radius: 2px;
          animation: expand 2s infinite alternate;
        }
        @keyframes expand { from { width: 30px; } to { width: 80px; } }

        /* Timeline */
        .timeline-wrapper { position: relative; }
        .timeline-horizontal {
          display: flex; gap: 20px; padding: 20px 10px; scroll-behavior: smooth; user-select: none; cursor: grab;
          overflow-x: auto; border-radius: 15px; background: linear-gradient(to right, #fff 10%, transparent 100%);
        }
        .timeline-horizontal:active { cursor: grabbing; }
        .timeline-item {
          flex: 0 0 260px; min-width: 260px; background: #f8f9fa; border-radius: 12px; padding: 25px 20px;
          box-shadow: 0 6px 18px rgba(0,0,0,0.1); text-align: center;
          transition: transform 0.4s, box-shadow 0.4s, background 0.4s;
        }
        .timeline-item.active {
          transform: scale(1.1); background: #fff3e6; box-shadow: 0 12px 30px rgba(0,0,0,0.25); border: 2px solid #ff6a00;
        }
        .timeline-item:hover { transform: translateY(-10px); box-shadow: 0 10px 25px rgba(0,0,0,0.2); }

        /* Arrows */
        .timeline-arrow {
          position: absolute; top: 50%; transform: translateY(-50%);
          background: rgba(0,0,0,0.5); border: none; color: white; font-size: 2rem;
          width: 50px; height: 50px; border-radius: 50%; cursor: pointer; z-index: 5;
          display: flex; align-items: center; justify-content: center; transition: all 0.3s ease;
        }
        .timeline-arrow:hover { background: rgba(0,0,0,0.8); transform: scale(1.1); }
        .timeline-arrow.left { left: 5px; }
        .timeline-arrow.right { right: 5px; }
        @media (max-width: 768px) { .timeline-arrow { display: none; } }

        /* CEO Card */
        .ceo-card { transition: all 0.4s ease-in-out; }
        .ceo-card:hover { transform: translateY(-5px) scale(1.02); box-shadow: 0px 12px 30px rgba(0,0,0,0.3); }

        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
